// SalesPerformanceDashboard.jsx

import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { ArrowLeft } from "react-bootstrap-icons";
import jwt_decode from "jwt-decode";
import config from 'react-global-configuration';
import DailySalesPerformance from "./DailySalesPerformance";
import DailySalesPerformanceDetailed from "./DailySalesPerformanceDetailed";
import DailyAcquisitionPerformance from "./DailyAcquisitionPerformance";
import DailyBeatPlanGTV from "./DailyBeatPlanGTV";
import DailyBeatPlanAdherence from "./DailyBeatPlanAdherence";
import "./SalesPerformanceDashboard.css";

/**
 * Helper to get the summary date. Uses yesterday's date for daily reports.
 * @returns {string} Yesterday's date in 'YYYY-MM-DD' format.
 */
const getYesterdayDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1); // Go back one day
    return date.toISOString().split('T')[0];
};

/**
 * Maps the raw API response for IC performance to the structure expected by DailySalesPerformanceDetailed.jsx
 * @param {object} apiData - The JSON response from ic_performance_report API
 * @returns {object} Mapped data object
 */
const mapDetailedICData = (apiData) => {
    const managerName = apiData.manager?.name || "N/A";
    const department = apiData.data[0]?.state || "N/A";

    const ics = apiData.data.map(ic => ({
        icId: ic.ic_emp_code,
        icName: ic.ic_name,
        managerName: ic.manager_name,
        totalAllocatedUTIDs: ic.total_allocated_utids,
        storeUTIDs: [],
        beatPlanAdherence: {
            liveToday: ic.beat_plan_adherence.live_today_pct,
            mtd: ic.beat_plan_adherence.mtd_pct
        },
        mtdAcquisition: {
            target: ic.mtd_acquisition.target,
            achieved: ic.mtd_acquisition.achieved,
            achievedPercent: ic.mtd_acquisition.achieved_pct
        },
        mtdBeatPlanGTV: {
            achieved: 0,
            achievedPercent: ic.mtd_beat_plan_gtv.achieved_pct
        },
        mtdOKR: {
            percentage: ic.mtd_okr.percentage
        }
    }));

    ics.sort((a, b) => b.mtdOKR.percentage - a.mtdOKR.percentage);

    const totals = {
        totalAllocatedUTIDs: ics.reduce((sum, i) => sum + i.totalAllocatedUTIDs, 0),
        beatPlanAdherence: {
            liveToday: ics.length > 0 ? (ics.reduce((sum, i) => sum + i.beatPlanAdherence.liveToday, 0) / ics.length).toFixed(0) : 0,
            mtd: ics.length > 0 ? (ics.reduce((sum, i) => sum + i.beatPlanAdherence.mtd, 0) / ics.length).toFixed(0) : 0,
        },
        mtdAcquisition: {
            target: ics.reduce((sum, i) => sum + i.mtdAcquisition.target, 0),
            achieved: ics.reduce((sum, i) => sum + i.mtdAcquisition.achieved, 0),
            achievedPercent: ics.length > 0 ? (ics.reduce((sum, i) => sum + i.mtdAcquisition.achievedPercent, 0) / ics.length).toFixed(0) : 0,
        },
        mtdBeatPlanGTV: {
            achieved: ics.reduce((sum, i) => sum + i.mtdBeatPlanGTV.achieved, 0),
            achievedPercent: ics.length > 0 ? (ics.reduce((sum, i) => sum + i.mtdBeatPlanGTV.achievedPercent, 0) / ics.length).toFixed(0) : 0
        },
        mtdOKR: {
            achievedPercent: ics.length > 0 ? (ics.reduce((sum, i) => sum + i.mtdOKR.percentage, 0) / ics.length).toFixed(0) : 0
        }
    };

    const summaryCounts = {
        icsInBottom10: 0,
        icsMeetingBeatAdherence80: ics.filter(ic => ic.beatPlanAdherence.mtd >= 80).length,
        icsNotMeetingBeatAdherence80: ics.filter(ic => ic.beatPlanAdherence.mtd < 80).length,
        icsMeetingAcquisition80: ics.filter(ic => ic.mtdAcquisition.achievedPercent >= 80).length,
        icsNotMeetingAcquisition80: ics.filter(ic => ic.mtdAcquisition.achievedPercent < 80).length,
        icsMeetingGTV80: ics.filter(ic => ic.mtdBeatPlanGTV.achievedPercent >= 80).length,
        icsNotMeetingGTV80: ics.filter(ic => ic.mtdBeatPlanGTV.achievedPercent < 80).length,
        icsOverallAchievement80: ics.filter(ic => ic.mtdOKR.percentage >= 80).length,
        icsOverallAchievementBelow80: ics.filter(ic => ic.mtdOKR.percentage < 80).length
    };

    return {
        managerName,
        department,
        ocrWeightage: {
            acquisition: `${apiData.extra_data.okr_weightage.acquisition}%`,
            beatPlanGTV: `${apiData.extra_data.okr_weightage.beat_plan_gtv}%`
        },
        summaryCounts,
        ics,
        totals
    };
};

/**
 * Maps the raw API response for Daily Acquisition Performance to the structure expected by DailyAcquisitionPerformance.jsx
 * @param {object} apiData - The JSON response from daily_acquisition_performance API
 * @returns {object} Mapped data object containing data, filter info, and report title
 */
const mapDailyAcquisitionData = (apiData) => {
    const filterInfo = apiData?.filter_info || {};
    const reportTitle = `${filterInfo?.manager_name ? `${filterInfo.manager_name}'s ` : 'All '}IC Daily Acquisition Performance`;

    const mappedData = apiData.data.map(ic => ({
        icName: ic.ic_name,
        managerName: ic.manager_name,
        acquisitionTarget: ic.monthly_target,
        t1: {
            visited: ic.t1.visited,
            acquired: ic.t1.acquired,
            lost: ic.t1.lost,
            followup: ic.t1.follow_up
        },
        wtd: {
            visited: ic.wtd.visited,
            acquired: ic.wtd.acquired,
            lost: ic.wtd.lost,
            followup: ic.wtd.follow_up
        },
        mtd: {
            visited: ic.mtd.visited,
            acquired: ic.mtd.acquired,
            lost: ic.mtd.lost,
            followup: ic.mtd.follow_up
        },
        achievement: ic.achievement_pct ? `${ic.achievement_pct.toFixed(2)}%` : null,
        productivity: ic.productivity ? `${ic.productivity.toFixed(2)}%` : null
    }));

    return {
        data: mappedData,
        filterInfo: filterInfo,
        reportTitle: reportTitle,
    };
};

/**
 * Maps the raw API response for Daily Beat Plan GTV Report to the structure expected by DailyBeatPlanGTV.jsx
 * @param {object} apiData - The JSON response from daily_beat_plan_gtv_report API
 * @returns {object} Mapped data object
 */
const mapDailyBeatPlanGTVData = (apiData) => {
    const filterInfo = apiData.filter_info;
    // Assume gtv_threshold is the same for all ICs in the report for the title
    const gtvThreshold = apiData.data[0]?.gtv_threshold?.toLocaleString('en-IN') || 'N/A';
    const reportTitle = "Daily Beat Plan GTV Report";

    const mappedData = apiData.data.map(ic => ({
        icName: ic.ic_name,
        managerName: ic.manager_name,
        storesAllocated: ic.total_beat_plan_stores_mtd,
        storesVisitedT1: ic.t1.stores_visited_above_threshold,
        storesVisitedWTD: ic.wtd.stores_visited_above_threshold,
        storesVisitedMTD: ic.mtd.stores_visited_above_threshold
    }));

    return {
        data: mappedData,
        filterInfo: filterInfo,
        reportTitle: reportTitle,
    };
};

/**
 * Maps the raw API response for Daily Beat Plan Adherence Report to the structure expected by DailyBeatPlanAdherence.jsx
 * @param {object} apiData - The JSON response from daily_beat_plan_adherence_report API
 * @returns {object} Mapped data object
 */
const mapDailyBeatPlanAdherenceData = (apiData) => {
    const filterInfo = apiData.filter_info;
    const reportTitle = `${filterInfo.manager_name ? `${filterInfo.manager_name}'s ` : 'All '}IC Daily Beat Plan Adherence Report`;

    const mappedData = apiData.data.map(ic => ({
        icName: ic.ic_name,
        managerName: ic.manager_name,
        // Format percentages to one decimal place with a '%' sign
        live: ic.live.adherence_pct ? `${ic.live.adherence_pct.toFixed(1)}%` : null,
        t1: ic.t1.adherence_pct ? `${ic.t1.adherence_pct.toFixed(1)}%` : null,
        t2: ic.t2.adherence_pct ? `${ic.t2.adherence_pct.toFixed(1)}%` : null,
        t3: ic.t3.adherence_pct ? `${ic.t3.adherence_pct.toFixed(1)}%` : null,
        wtd: ic.wtd.adherence_pct ? `${ic.wtd.adherence_pct.toFixed(1)}%` : null,
        mtd: ic.mtd.adherence_pct ? `${ic.mtd.adherence_pct.toFixed(1)}%` : null,
    }));

    return {
        data: mappedData,
        filterInfo: filterInfo,
        reportTitle: reportTitle,
    };
};


const SalesPerformanceDashboard = (props) => {
    const { history } = props;

    const [currentView, setCurrentView] = useState("main");
    const [loading, setLoading] = useState(false);
    const [userRole, setUserRole] = useState(null); // 'manager' or 'ic'
    const [userEmpCode, setUserEmpCode] = useState(null);

    const [mainTableData, setMainTableData] = useState(null);
    const [detailedData, setDetailedData] = useState(null);
    const [acquisitionData, setAcquisitionData] = useState(null);
    const [beatPlanGTVData, setBeatPlanGTVData] = useState(null);
    const [beatPlanAdherenceData, setBeatPlanAdherenceData] = useState(null);
    const [selectedFilterType, setSelectedFilterType] = useState(null);
    const [activeFilterUI, setActiveFilterUI] = useState(null);
    const [selectedManagerCode, setSelectedManagerCode] = useState("");
    const navigationHistoryRef = useRef([]);

    // Decode token and determine user role
    useEffect(() => {
        try {
            const token = localStorage.getItem("usertoken");
            if (token) {
                const decoded = jwt_decode(token);
                console.log("Decoded token:", decoded);

                // Check if user is a manager (has manager_of_aso)
                if (decoded.manager_of_aso) {
                    setUserRole('manager');
                    setUserEmpCode(decoded.manager_of_aso);
                    console.log("User is a manager with code:", decoded.manager_of_aso);
                }
                // Check if user is an IC (has aso_emp_code but no manager_of_aso)
                else if (decoded.aso_emp_code) {
                    setUserRole('ic');
                    setUserEmpCode(decoded.aso_emp_code);
                    console.log("User is an IC with code:", decoded.aso_emp_code);
                }
                else {
                    // Default to manager view for admin users or users without specific codes
                    setUserRole('manager');
                    setUserEmpCode(null);
                    console.log("User role defaulted to manager (admin user or no specific code)");
                }
            }
        } catch (error) {
            console.error("Error decoding token:", error);
            setUserRole('manager'); // Default to manager view on error
            setUserEmpCode(null);
        }
    }, []);

    // Helper to fetch manager data (Main View)
    const fetchManagerData = async () => {
        setLoading(true);
        try {
            const summaryDate = getYesterdayDate();
            const apiUrl = config.get("API_URL") + "/summaryReport/manager_performance_report";
            const requestBody = {
                summary_date: summaryDate,
                manager_emp_code: userEmpCode || "",
                ic_emp_code: "",
                filter_type: userEmpCode ? "manager" : "admin"
            };

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch sales performance data.");
            }

            // Data mapping logic for main table
            const managers = apiData.data.map(mgr => ({
                managerId: mgr.manager_emp_code,
                managerName: mgr.manager_name,
                department: mgr.state || "N/A",
                totalAllocatedUTIDs: mgr.total_allocated_utids,
                numICs: mgr.num_ics,
                mtdAcquisition: {
                    target: mgr.mtd_acquisition.target,
                    achieved: mgr.mtd_acquisition.achieved
                },
                mtdICComposite: {
                    bucket0: mgr.ic_composite_performance.band_0_pct,
                    bucketUpTo40: mgr.ic_composite_performance.band_0_40_pct,
                    bucketUpTo80: mgr.ic_composite_performance.band_40_80_pct,
                    bucketAbove80: mgr.ic_composite_performance.band_80_plus_pct
                },
                lowestICPerformance: `${mgr.lowest_ic_performance.okr_percentage}%`,
                mtdBeatPlanGTV: {
                    achieved: null,
                    achievedPercent: mgr.mtd_beat_plan_gtv_pct
                },
                okrStatus: {
                    blendedPercent: mgr.okr_status.percentage
                }
            }));

            const totals = {
                totalAllocatedUTIDs: apiData.total.total_allocated_utids,
                numICs: apiData.total.num_ics,
                mtdAcquisition: {
                    target: apiData.total.mtd_acquisition.target,
                    achieved: apiData.total.mtd_acquisition.achieved
                },
                mtdICComposite: {
                    bucket0: apiData.total.ic_composite_performance.band_0_pct,
                    bucketUpTo40: apiData.total.ic_composite_performance.band_0_40_pct,
                    bucketUpTo80: apiData.total.ic_composite_performance.band_40_80_pct,
                    bucketAbove80: apiData.total.ic_composite_performance.band_80_plus_pct
                },
                lowestManagerPerformance: apiData.total.note || "Lowest Manager Performance",
                mtdBeatPlanGTV: {
                    achieved: null,
                    achievedPercent: null
                },
                okrStatus: {
                    blendedPercent: null
                }
            };

            managers.sort((a, b) => b.okrStatus.blendedPercent - a.okrStatus.blendedPercent);

            const mappedData = {
                data: { managers, totals },
                ocrWeightage: {
                    lowestIC: apiData.extra_data?.okr_weightage?.lowest_ic
                        ? `${apiData.extra_data.okr_weightage.lowest_ic}%`
                        : "",
                    acquisition: apiData.extra_data?.okr_weightage?.acquisition
                        ? `${apiData.extra_data.okr_weightage.acquisition}%`
                        : ""
                }
            };

            setMainTableData(mappedData);

        } catch (error) {
            console.error("Error loading main data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Initial data load - load appropriate table based on user role
    useEffect(() => {
        if (!userRole) return; // Wait for role to be determined

        // Check if returning from Team Dashboard and restore IC table view
        // We handle this here to ensure userRole is ready before calling handleTotalClickThrough
        if (history.location.state?.returnToICTable) {
            handleTotalClickThrough().finally(() => {
                // Clear the state to prevent re-triggering
                history.replace({ ...history.location, state: {} });
            });
            return;
        }

        const loadMainData = async () => {
            setLoading(true);
            try {
                const summaryDate = getYesterdayDate();

                // If user is an IC, load IC performance directly
                if (userRole === 'ic') {
                    const apiUrl = config.get("API_URL") + "/summaryReport/ic_performance_report";
                    const requestBody = {
                        summary_date: summaryDate,
                        manager_emp_code: "",
                        ic_emp_code: userEmpCode,
                        filter_type: "ic"
                    };

                    const response = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            authorization: `${localStorage.getItem("usertoken")}`,
                        },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const apiData = await response.json();

                    if (apiData.error || !apiData.data) {
                        throw new Error(apiData.error || "Failed to fetch IC performance data.");
                    }

                    // Filter to show only the logged-in IC's data
                    const filteredData = {
                        ...apiData,
                        data: apiData.data.filter(ic => ic.ic_emp_code === userEmpCode)
                    };

                    const mappedData = mapDetailedICData(filteredData);
                    setDetailedData(mappedData);
                    setCurrentView("detailed"); // Show IC view directly
                    setLoading(false);
                    return;
                }

                // Otherwise, load manager performance (for managers and admins)
                await fetchManagerData();
            } catch (error) {
                console.error("Error loading main data:", error);
            } finally {
                setLoading(false);
            }
        };
        loadMainData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, userEmpCode]);

    // Check if returning from Team Dashboard and restore IC table view
    // Handled in the initial data load effect now to ensure proper data fetching
    // useEffect(() => {
    //     if (history.location.state?.returnToICTable) {
    //         setCurrentView("detailed");
    //         // Clear the state to prevent re-triggering
    //         history.replace({ ...history.location, state: {} });
    //     }
    // }, [history.location.state]);

    // Handles click on a Manager Name in the main table to show detailed IC data
    const handleManagerNameClick = async (managerId) => {
        setLoading(true);
        setSelectedManagerCode(managerId); // Track the selected manager

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/ic_performance_report";
        const requestBody = {
            summary_date: summaryDate,
            manager_emp_code: managerId,
            ic_emp_code: "",
            filter_type: selectedFilterType || "manager"
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || `Failed to fetch IC performance data for manager ${managerId}.`);
            }

            const mappedData = mapDetailedICData(apiData);

            setDetailedData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("detailed");

        } catch (error) {
            console.error("Error fetching detailed data :", error);
        } finally {
            setLoading(false);
        }
    };

    // Handles click on the Total row in the main table to show detailed IC data for all managers
    const handleTotalClickThrough = async () => {
        setLoading(true);
        setSelectedManagerCode(""); // Empty string for all managers

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/ic_performance_report";

        // Build request body based on user role
        // Admin: empty strings, Manager: manager_emp_code = userEmpCode, IC: ic_emp_code = userEmpCode
        const requestBody = {
            summary_date: summaryDate,
            manager_emp_code: userRole === 'manager' && userEmpCode ? userEmpCode : "",
            ic_emp_code: userRole === 'ic' && userEmpCode ? userEmpCode : "",
            filter_type: selectedFilterType || (userRole === 'manager' && userEmpCode ? "manager" : userRole === 'ic' && userEmpCode ? "ic" : "admin")
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch IC performance data for all managers.");
            }

            let mappedData = mapDetailedICData(apiData);

            mappedData = {
                ...mappedData,
                managerName: "All ICs",
                department: "All Departments"
            };

            setDetailedData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("detailed");

        } catch (error) {
            console.error("Error fetching detailed data (ALL) :", error);
        } finally {
            setLoading(false);
        }
    };

    // Handles the general row click on Manager or IC row to navigate to the Team Dashboard
    const handleRowClickThrough = (data) => {
        // Handle both object (from Manager table) and string ID (from IC table)
        let icEmpCode = null;
        if (typeof data === 'string') {
            icEmpCode = data;
        } else if (data && data.icId) {
            icEmpCode = data.icId;
        } else if (data && data.managerId) {
            // If it's a manager row click, we might want to pass all ICs or just managerId
            // For now, consistent with previous behavior, but fixed the icId check
            icEmpCode = null; // Manager clicks usually go to detailed view first
        }

        // Pass both userEmpCode (for filter APIs) and selectedICEmpCode (for genie transaction API)
        // userEmpCode is used for get-team, get-store, get-city (to show manager's full team/stores/cities)
        // selectedICEmpCode is used for genie_team_dashboard API (to show only selected IC's data)
        history.push({
            pathname: "/admin/team-dashboard",
            state: {
                selectedICEmpCode: icEmpCode, // Pass IC employee code for genie transaction API
                userEmpCode: selectedManagerCode || userEmpCode, // Pass manager's emp code for filter APIs
                fromSalesPerformance: true
            }
        });
    };

    // Handles total row click in the detailed IC table to navigate to Team Dashboard with the manager's team
    const handleTotalRowClickThrough = () => {
        // For admin users (no aso_emp_code and no manager_of_aso), don't pass any userEmpCode
        // This ensures admin sees all data in filter APIs, even if they clicked on a manager first
        // For manager/IC users, pass their emp code
        let teamId = null;

        if (userRole === 'manager' || userRole === 'ic') {
            // Non-admin user - pass the selected manager code or their own emp code
            teamId = selectedManagerCode || userEmpCode;
        }
        // For admin users, teamId remains null

        history.push({
            pathname: "/admin/team-dashboard",
            state: {
                userEmpCode: teamId, // null for admin, emp code for manager/IC
                fromSalesPerformance: true
            }
        });
    };

    /**
     * Handles the click on the MTD Acquisition column (Table 3), fetching the daily acquisition performance data
     * from the API for all ICs.
     */
    const handleMTDAcquisitionClick = async (manager = null) => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_acquisition_performance";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: manager ? "manager" : "admin",
            ic_emp_code: "",
            manager_emp_code: manager ? manager.managerId : ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch acquisition performance data.");
            }

            const mappedData = mapDailyAcquisitionData(apiData);
            setAcquisitionData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("acquisition");

        } catch (error) {
            console.error("Error fetching acquisition data :", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles the click on the MTD Beat Plan GTV column (Table 4), fetching the daily beat plan GTV data
     * from the API for all ICs.
     */
    const handleMTDBeatPlanGTVClick = async (manager = null) => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_beat_plan_gtv_report";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: manager ? "manager" : "admin",
            ic_emp_code: "",
            manager_emp_code: manager ? manager.managerId : ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch beat plan GTV data.");
            }

            const mappedData = mapDailyBeatPlanGTVData(apiData);
            setBeatPlanGTVData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("beatPlanGTV");

        } catch (error) {
            console.error("Error fetching beat plan GTV data :", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles the click on MTD Acquisition from IC table (Table 2)
     * Navigates to Acquisition Report filtered for that specific IC
     */
    const handleICAcquisitionClick = async (ic) => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_acquisition_performance";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: "ic",
            ic_emp_code: ic.icId,
            manager_emp_code: ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch acquisition performance data for IC.");
            }

            const mappedData = mapDailyAcquisitionData(apiData);
            setAcquisitionData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("acquisition");

        } catch (error) {
            console.error("Error fetching acquisition data for IC:", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles the click on Beat Plan GTV from IC table (Table 2)
     * Navigates to Beat Plan GTV Report filtered for that specific IC
     */
    const handleICBeatPlanGTVClick = async (ic) => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_beat_plan_gtv_report";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: "ic",
            ic_emp_code: ic.icId,
            manager_emp_code: ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch beat plan GTV data for IC.");
            }

            const mappedData = mapDailyBeatPlanGTVData(apiData);
            setBeatPlanGTVData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("beatPlanGTV");

        } catch (error) {
            console.error("Error fetching beat plan GTV data for IC:", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles the click on the Beat Plan Adherence cell for a specific IC in Table 2
     * Navigates to Beat Plan Adherence Report filtered for that IC
     */
    const handleICBeatPlanAdherenceClick = async (ic) => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_beat_plan_adherence_report";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: "ic",
            ic_emp_code: ic.icId,
            manager_emp_code: ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch beat plan adherence data.");
            }

            const mappedData = mapDailyBeatPlanAdherenceData(apiData);
            setBeatPlanAdherenceData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("beatPlanAdherence");

        } catch (error) {
            console.error("Error fetching beat plan adherence data for IC:", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles the click on the Beat Plan Adherence column (Table 5), fetching the daily adherence data
     * from the API for all ICs. This completes the API integration for all summary tables.
     */
    const handleBeatPlanAdherenceClick = async () => {
        setLoading(true);

        const summaryDate = getYesterdayDate();
        const apiUrl = config.get("API_URL") + "/summaryReport/daily_beat_plan_adherence_report";

        const requestBody = {
            summary_date: summaryDate,
            filter_type: "admin",
            ic_emp_code: "",
            manager_emp_code: ""
        };

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `${localStorage.getItem("usertoken")}`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const apiData = await response.json();

            if (apiData.error || !apiData.data) {
                throw new Error(apiData.error || "Failed to fetch beat plan adherence data.");
            }

            const mappedData = mapDailyBeatPlanAdherenceData(apiData);
            setBeatPlanAdherenceData(mappedData);

            // Push current state to history before changing view
            navigationHistoryRef.current.push({
                view: currentView,
                selectedManagerCode,
                selectedFilterType,
                activeFilterUI
            });
            setCurrentView("beatPlanAdherence");

        } catch (error) {
            console.error("Error fetching beat plan adherence data :", error);
        } finally {
            setLoading(false);
        }
    };

    // Switches the view back to the appropriate previous view
    // ICs should not be able to go back to the main view
    const handleBackClick = () => {
        // If user is an IC and on detailed view, don't allow going back to main
        if (userRole === 'ic' && currentView === 'detailed') {
            return; // Do nothing
        }

        // Pop the last view from history
        if (navigationHistoryRef.current.length > 0) {
            const previousState = navigationHistoryRef.current.pop();

            // If going back to main view and data is missing (e.g. redirected from back nav), fetch it
            if (previousState.view === 'main' && !mainTableData && userRole !== 'ic') {
                fetchManagerData();
            }

            setCurrentView(previousState.view);

            // Restore the previous state
            if (previousState.selectedManagerCode !== undefined) {
                setSelectedManagerCode(previousState.selectedManagerCode);
            }
            if (previousState.selectedFilterType !== undefined) {
                setSelectedFilterType(previousState.selectedFilterType);
            }
            if (previousState.activeFilterUI !== undefined) {
                setActiveFilterUI(previousState.activeFilterUI);
            }
        } else {
            // Fallback to default behavior if no history
            if (currentView === 'beatPlanAdherence' || currentView === 'acquisition' || currentView === 'beatPlanGTV') {
                setCurrentView("detailed");
            } else {
                setCurrentView("main");
            }
        }
    };

    // Handle filter change from DailySalesPerformanceDetailed
    const handleFilterChange = async (filterType, filterUIName) => {
        setSelectedFilterType(filterType);
        setActiveFilterUI(filterUIName);

        // Re-fetch IC performance data with the new filter
        if (currentView === "detailed" && detailedData) {
            setLoading(true);
            const summaryDate = getYesterdayDate();
            const apiUrl = config.get("API_URL") + "/summaryReport/ic_performance_report";

            // Use the tracked manager code
            const managerCode = selectedManagerCode;

            const requestBody = {
                summary_date: summaryDate,
                manager_emp_code: managerCode,
                ic_emp_code: "",
                filter_type: filterType || (managerCode ? "manager" : "admin")
            };

            try {
                const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `${localStorage.getItem("usertoken")}`,
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const apiData = await response.json();

                if (apiData.error || !apiData.data) {
                    throw new Error(apiData.error || "Failed to fetch filtered IC performance data.");
                }

                const mappedData = mapDetailedICData(apiData);
                setDetailedData(mappedData);
            } catch (error) {
                console.error("Error fetching filtered IC performance data:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Render header based on current view
    const renderHeader = () => {
        if (currentView !== "main") {
            // For ICs on the detailed view (their default), don't show back button
            const showBackButton = !(userRole === 'ic' && currentView === 'detailed');

            return (
                <div className="spd-header-with-back">
                    {showBackButton && (
                        <button className="btn btn-secondary mb-3" onClick={handleBackClick}>
                            <ArrowLeft /> Back
                        </button>
                    )}
                    <h3>{getViewTitle()}</h3>
                </div>
            );
        }
        return (
            <div className="spd-header">
                <h2>Sales Performance Dashboard</h2>
                <p>Daily sales performance metrics and team analytics</p>
            </div>
        );
    };

    // Dynamically sets the title based on the current view
    const getViewTitle = () => {
        switch (currentView) {
            case "detailed":
                return "Daily Sales Performance - IC Breakdown";
            case "acquisition":
                return acquisitionData?.reportTitle || "Daily Acquisition Performance Report";
            case "beatPlanGTV":
                return beatPlanGTVData?.reportTitle || "Daily Beat Plan GTV Report";
            case "beatPlanAdherence":
                return beatPlanAdherenceData?.reportTitle || "Daily Beat Plan Adherence Report";
            default:
                return "Sales Performance Dashboard";
        }
    };

    return (
        <div className="spd-container">
            {renderHeader()}

            {loading && (
                <div className="text-center my-4">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}

            {!loading && (
                <>
                    {/* Main Manager Performance Table */}
                    {currentView === "main" && (
                        <DailySalesPerformance
                            data={mainTableData}
                            onManagerClick={handleManagerNameClick}
                            onMTDAcquisitionClick={handleMTDAcquisitionClick}
                            onMTDBeatPlanGTVClick={handleMTDBeatPlanGTVClick}
                            onBeatPlanAdherenceClick={handleBeatPlanAdherenceClick}
                            onRowClickThrough={handleRowClickThrough}
                            onTotalClickThrough={handleTotalClickThrough}
                        />
                    )}

                    {/* Detailed IC Performance Table */}
                    {currentView === "detailed" && (
                        <DailySalesPerformanceDetailed
                            data={detailedData}
                            onICNameClick={handleRowClickThrough}
                            onTotalRowClickThrough={handleTotalRowClickThrough}
                            onBeatPlanAdherenceClick={handleICBeatPlanAdherenceClick}
                            onFilterChange={handleFilterChange}
                            activeFilter={activeFilterUI}
                            onMTDAcquisitionClick={handleICAcquisitionClick}
                            onMTDBeatPlanGTVClick={handleICBeatPlanGTVClick}
                        />
                    )}

                    {/* Daily Acquisition Performance Table */}
                    {currentView === "acquisition" && (
                        <DailyAcquisitionPerformance data={acquisitionData} />
                    )}

                    {/* Daily Beat Plan GTV Report */}
                    {currentView === "beatPlanGTV" && (
                        <DailyBeatPlanGTV data={beatPlanGTVData} />
                    )}

                    {/* Daily Beat Plan Adherence Report */}
                    {currentView === "beatPlanAdherence" && (
                        <DailyBeatPlanAdherence data={beatPlanAdherenceData} />
                    )}
                </>
            )}
        </div>
    );
};

export default withRouter(SalesPerformanceDashboard);